import styled, { css, keyframes } from 'styled-components'
import { device, fonts } from '../../../../styles/GlobalVariables'
import Link from '../../../Link/Link'
import ReactPlayer from 'react-player'
import {
  loaderTurn,
  storiesLoaderRing,
} from '../../Stories/storiesLoader.style'

/** an ad that loads faster than this shows no spinner (same as the clips) */
const AD_SPINNER_DELAY_MS = 600
const AD_SPINNER_SIZE_PX = 40

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`
const appear = keyframes`
  to {
    opacity: 1;
  }
`

/** an ad that is still loading, so the slide does not look frozen */
export const AdLoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${AD_SPINNER_SIZE_PX}px;
  height: ${AD_SPINNER_SIZE_PX}px;
  margin: -${AD_SPINNER_SIZE_PX / 2}px 0 0 -${AD_SPINNER_SIZE_PX / 2}px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  opacity: 0;
  animation: ${appear} 0.2s ease ${AD_SPINNER_DELAY_MS}ms forwards,
    ${spin} 0.9s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: ${appear} 0.2s ease ${AD_SPINNER_DELAY_MS}ms forwards;
  }
`

export const Container = styled.div<{ $fill?: boolean; $wide?: boolean }>`
  position: relative;
  width: 100%;
  height: calc(100dvh - env(safe-area-inset-bottom)); // was auto
  display: flex;
  align-items: center;
  background-color: #2b2b2b;
  overflow: hidden;

  ${device.medium} {
    position: relative;
    height: 80vh; // for some reason 90vh doesn't work and make the video not pressable
    aspect-ratio: 9/16;
    width: calc(80vh * 9 / 16);
  }

  ${props =>
    props.$fill &&
    css`
      /* the viewer sizes the box. A vertical ad fills it, as the design draws
         it; a landscape one is letterboxed, never cropped */
      height: 100%;
      background-color: #000;

      ${device.medium} {
        height: 100%;
        width: 100%;
        aspect-ratio: auto;
      }

      video {
        object-fit: ${props.$wide ? 'contain' : 'cover'};
      }

      /* the design's loader, the same as the clips' */
      ${AdLoadingSpinner} {
        ${storiesLoaderRing}
        inset: 0;
        margin: auto;
        animation: ${appear} 0.2s ease ${AD_SPINNER_DELAY_MS}ms forwards,
          ${loaderTurn} 1.2s linear infinite;

        @media (prefers-reduced-motion: reduce) {
          animation: ${appear} 0.2s ease ${AD_SPINNER_DELAY_MS}ms forwards;
        }
      }
    `}
`
export const Player = styled(ReactPlayer)`
  aspect-ratio: 9/16;
  margin: auto;
  background: transparent;
  width: 100%;
`
export const PlayerAdOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 59.99%, #000 103.03%);

  ${device.small} {
    &:hover,
    &:active {
      cursor: pointer;
    }
  }
`
export const TopOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 12px;
  direction: rtl;

  ${device.medium} {
    margin: 10px 10px;
  }
`
/** real buttons (keyboard, screen readers) that look like the old divs */
const iconButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`
export const TopCloseContainer = styled.button`
  ${iconButton}
`
export const TopMuteContainer = styled.button`
  ${iconButton}
`
export const PlayIconOverlay = styled.div`
  position: absolute;
  top: calc(50% - 31px);
  right: calc(50% - 29px);
  bottom: 0;
  width: fit-content;
  height: fit-content;

  ${device.medium} {
    top: 50%;
  }
`
export const GuiArea = styled.div`
  position: relative;
  flex: 1;
`
export const BottomOverlay = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  direction: rtl;
  gap: 16px;
  margin: 0 12px 10px;

  ${device.medium} {
    gap: 30px;
    margin: 0 12px 30px;
  }
`
export const MoreInfo = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  border-radius: 4px;
  background: rgba(128, 128, 128, 0.5);
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  gap: 4px;
  padding: 4px 12px;

  ${device.medium} {
    gap: 6px;
    padding: 8px 16px;
  }

  div {
    color: #fff;
    font-size: 16px;
    font-family: ${fonts.narkis};
    font-weight: 700;
    text-align: right;

    ${device.medium} {
      font-size: 20px;
    }
  }
`
export const ProgressContainer = styled.div`
  display: flex;
  height: 20px;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  ${device.medium} {
    height: 22px;
  }
`
export const ProgressBarContainer = styled.div`
  background: #ffffff66;
  width: 100%;
  height: 6px;
  position: relative;
  border-radius: 20px;
  direction: ltr;
`
export const Progress = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: #ffffff;
  position: absolute;
  border-radius: 20px;
`
